import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { BaseEmployeeSelection, StandardEmployeeSelection } from '@/hooks/employees/useUnifiedEmployeeSelection';

// Global employee selection state
export interface EmployeeSelectionContextState {
  // Multi-context selection support
  selections: Record<string, {
    employeeIds: string[];
    context: string; // e.g., 'shift-assignment', 'bulk-operations', 'communications'
    timestamp: number;
  }>;
  
  // Current active context
  activeContext: string | null;
  
  // Global employee cache to prevent duplicate API calls
  employeeCache: Record<string, {
    employees: StandardEmployeeSelection[];
    lastFetched: number;
    businessId: string;
  }>;
  
  // Availability checking cache
  availabilityCache: Record<string, {
    employeeId: string;
    isAvailable: boolean;
    conflicts: any[];
    timeRange: {
      start: Date;
      end: Date;
    };
    lastChecked: number;
  }>;
  
  // Drag and drop state
  dragDropState: {
    isDragging: boolean;
    draggedEmployee: StandardEmployeeSelection | null;
    dragContext: string | null;
  };
}

// Context actions
export interface EmployeeSelectionContextActions {
  // Selection management
  setSelection: (context: string, employeeIds: string[]) => void;
  addToSelection: (context: string, employeeId: string) => void;
  removeFromSelection: (context: string, employeeId: string) => void;
  toggleSelection: (context: string, employeeId: string) => void;
  clearSelection: (context: string) => void;
  clearAllSelections: () => void;
  
  // Context management
  setActiveContext: (context: string | null) => void;
  getSelection: (context: string) => string[];
  hasSelection: (context: string) => boolean;
  
  // Employee cache management
  cacheEmployees: (businessId: string, employees: StandardEmployeeSelection[]) => void;
  getCachedEmployees: (businessId: string) => StandardEmployeeSelection[] | null;
  invalidateEmployeeCache: (businessId?: string) => void;
  
  // Availability cache management
  cacheAvailability: (employeeId: string, isAvailable: boolean, conflicts: any[], timeRange: { start: Date; end: Date }) => void;
  getCachedAvailability: (employeeId: string, timeRange: { start: Date; end: Date }) => { isAvailable: boolean; conflicts: any[] } | null;
  invalidateAvailabilityCache: (employeeId?: string) => void;
  
  // Drag and drop management
  setDragState: (isDragging: boolean, employee?: StandardEmployeeSelection | null, context?: string | null) => void;
  getDragState: () => { isDragging: boolean; draggedEmployee: StandardEmployeeSelection | null; dragContext: string | null };
}

// Combined context type
export type EmployeeSelectionContextType = EmployeeSelectionContextState & EmployeeSelectionContextActions;

// Create context
const EmployeeSelectionContext = createContext<EmployeeSelectionContextType | undefined>(undefined);

// Cache constants
const EMPLOYEE_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const AVAILABILITY_CACHE_DURATION = 2 * 60 * 1000; // 2 minutes

// Provider component
export const EmployeeSelectionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<EmployeeSelectionContextState>({
    selections: {},
    activeContext: null,
    employeeCache: {},
    availabilityCache: {},
    dragDropState: {
      isDragging: false,
      draggedEmployee: null,
      dragContext: null,
    },
  });

  // Selection management
  const setSelection = useCallback((context: string, employeeIds: string[]) => {
    setState(prev => ({
      ...prev,
      selections: {
        ...prev.selections,
        [context]: {
          employeeIds,
          context,
          timestamp: Date.now(),
        },
      },
    }));
  }, []);

  const addToSelection = useCallback((context: string, employeeId: string) => {
    setState(prev => {
      const existing = prev.selections[context]?.employeeIds || [];
      if (existing.includes(employeeId)) return prev;
      
      return {
        ...prev,
        selections: {
          ...prev.selections,
          [context]: {
            employeeIds: [...existing, employeeId],
            context,
            timestamp: Date.now(),
          },
        },
      };
    });
  }, []);

  const removeFromSelection = useCallback((context: string, employeeId: string) => {
    setState(prev => {
      const existing = prev.selections[context]?.employeeIds || [];
      const filtered = existing.filter(id => id !== employeeId);
      
      return {
        ...prev,
        selections: {
          ...prev.selections,
          [context]: {
            employeeIds: filtered,
            context,
            timestamp: Date.now(),
          },
        },
      };
    });
  }, []);

  const toggleSelection = useCallback((context: string, employeeId: string) => {
    setState(prev => {
      const existing = prev.selections[context]?.employeeIds || [];
      const isSelected = existing.includes(employeeId);
      
      return {
        ...prev,
        selections: {
          ...prev.selections,
          [context]: {
            employeeIds: isSelected
              ? existing.filter(id => id !== employeeId)
              : [...existing, employeeId],
            context,
            timestamp: Date.now(),
          },
        },
      };
    });
  }, []);

  const clearSelection = useCallback((context: string) => {
    setState(prev => ({
      ...prev,
      selections: {
        ...prev.selections,
        [context]: {
          employeeIds: [],
          context,
          timestamp: Date.now(),
        },
      },
    }));
  }, []);

  const clearAllSelections = useCallback(() => {
    setState(prev => ({
      ...prev,
      selections: {},
    }));
  }, []);

  // Context management
  const setActiveContext = useCallback((context: string | null) => {
    setState(prev => ({
      ...prev,
      activeContext: context,
    }));
  }, []);

  const getSelection = useCallback((context: string) => {
    return state.selections[context]?.employeeIds || [];
  }, [state.selections]);

  const hasSelection = useCallback((context: string) => {
    const selection = state.selections[context];
    return selection && selection.employeeIds.length > 0;
  }, [state.selections]);

  // Employee cache management
  const cacheEmployees = useCallback((businessId: string, employees: StandardEmployeeSelection[]) => {
    setState(prev => ({
      ...prev,
      employeeCache: {
        ...prev.employeeCache,
        [businessId]: {
          employees,
          lastFetched: Date.now(),
          businessId,
        },
      },
    }));
  }, []);

  const getCachedEmployees = useCallback((businessId: string): StandardEmployeeSelection[] | null => {
    const cached = state.employeeCache[businessId];
    if (!cached) return null;
    
    const isExpired = Date.now() - cached.lastFetched > EMPLOYEE_CACHE_DURATION;
    return isExpired ? null : cached.employees;
  }, [state.employeeCache]);

  const invalidateEmployeeCache = useCallback((businessId?: string) => {
    setState(prev => {
      if (businessId) {
        const { [businessId]: removed, ...rest } = prev.employeeCache;
        return { ...prev, employeeCache: rest };
      } else {
        return { ...prev, employeeCache: {} };
      }
    });
  }, []);

  // Availability cache management
  const cacheAvailability = useCallback((
    employeeId: string, 
    isAvailable: boolean, 
    conflicts: any[], 
    timeRange: { start: Date; end: Date }
  ) => {
    const cacheKey = `${employeeId}-${timeRange.start.getTime()}-${timeRange.end.getTime()}`;
    setState(prev => ({
      ...prev,
      availabilityCache: {
        ...prev.availabilityCache,
        [cacheKey]: {
          employeeId,
          isAvailable,
          conflicts,
          timeRange,
          lastChecked: Date.now(),
        },
      },
    }));
  }, []);

  const getCachedAvailability = useCallback((
    employeeId: string, 
    timeRange: { start: Date; end: Date }
  ): { isAvailable: boolean; conflicts: any[] } | null => {
    const cacheKey = `${employeeId}-${timeRange.start.getTime()}-${timeRange.end.getTime()}`;
    const cached = state.availabilityCache[cacheKey];
    
    if (!cached) return null;
    
    const isExpired = Date.now() - cached.lastChecked > AVAILABILITY_CACHE_DURATION;
    return isExpired ? null : { isAvailable: cached.isAvailable, conflicts: cached.conflicts };
  }, [state.availabilityCache]);

  const invalidateAvailabilityCache = useCallback((employeeId?: string) => {
    setState(prev => {
      if (employeeId) {
        const filtered = Object.fromEntries(
          Object.entries(prev.availabilityCache).filter(
            ([key]) => !key.startsWith(employeeId)
          )
        );
        return { ...prev, availabilityCache: filtered };
      } else {
        return { ...prev, availabilityCache: {} };
      }
    });
  }, []);

  // Drag and drop management
  const setDragState = useCallback((
    isDragging: boolean, 
    employee?: StandardEmployeeSelection | null, 
    context?: string | null
  ) => {
    setState(prev => ({
      ...prev,
      dragDropState: {
        isDragging,
        draggedEmployee: employee || null,
        dragContext: context || null,
      },
    }));
  }, []);

  const getDragState = useCallback(() => {
    return state.dragDropState;
  }, [state.dragDropState]);

  // Context value
  const contextValue = useMemo<EmployeeSelectionContextType>(() => ({
    // State
    ...state,
    
    // Actions
    setSelection,
    addToSelection,
    removeFromSelection,
    toggleSelection,
    clearSelection,
    clearAllSelections,
    setActiveContext,
    getSelection,
    hasSelection,
    cacheEmployees,
    getCachedEmployees,
    invalidateEmployeeCache,
    cacheAvailability,
    getCachedAvailability,
    invalidateAvailabilityCache,
    setDragState,
    getDragState,
  }), [
    state,
    setSelection,
    addToSelection,
    removeFromSelection,
    toggleSelection,
    clearSelection,
    clearAllSelections,
    setActiveContext,
    getSelection,
    hasSelection,
    cacheEmployees,
    getCachedEmployees,
    invalidateEmployeeCache,
    cacheAvailability,
    getCachedAvailability,
    invalidateAvailabilityCache,
    setDragState,
    getDragState,
  ]);

  return (
    <EmployeeSelectionContext.Provider value={contextValue}>
      {children}
    </EmployeeSelectionContext.Provider>
  );
};

// Hook to use the context
export const useEmployeeSelectionContext = (): EmployeeSelectionContextType => {
  const context = useContext(EmployeeSelectionContext);
  if (context === undefined) {
    throw new Error('useEmployeeSelectionContext must be used within an EmployeeSelectionProvider');
  }
  return context;
};

// Convenience hooks for specific use cases
export const useEmployeeSelectionForContext = (contextName: string) => {
  const {
    getSelection,
    setSelection,
    addToSelection,
    removeFromSelection,
    toggleSelection,
    clearSelection,
    hasSelection,
    setActiveContext,
  } = useEmployeeSelectionContext();

  return useMemo(() => ({
    selectedIds: getSelection(contextName),
    setSelectedIds: (ids: string[]) => setSelection(contextName, ids),
    addEmployee: (id: string) => addToSelection(contextName, id),
    removeEmployee: (id: string) => removeFromSelection(contextName, id),
    toggleEmployee: (id: string) => toggleSelection(contextName, id),
    clearSelection: () => clearSelection(contextName),
    hasSelection: hasSelection(contextName),
    activateContext: () => setActiveContext(contextName),
  }), [
    contextName,
    getSelection,
    setSelection,
    addToSelection,
    removeFromSelection,
    toggleSelection,
    clearSelection,
    hasSelection,
    setActiveContext,
  ]);
};

// Drag and drop convenience hook
export const useEmployeeDragDrop = () => {
  const { setDragState, getDragState } = useEmployeeSelectionContext();
  
  return useMemo(() => ({
    startDrag: (employee: StandardEmployeeSelection, context: string) => 
      setDragState(true, employee, context),
    stopDrag: () => setDragState(false, null, null),
    getDragState,
    get isDragging() { return getDragState().isDragging; },
    get draggedEmployee() { return getDragState().draggedEmployee; },
    get dragContext() { return getDragState().dragContext; },
  }), [setDragState, getDragState]);
};